// functions/drivers_page/drivers_page_profile_functions.jsx

// Import API routes
import { fetch_all_profiles, suspend_profile, update_profile, reset_password, reset_totp } from "../../api/profiles";
import { fetch_all_customers } from "../../api/customers";
import { fetch_all_vehicles } from "../../api/vehicles";
import { validSession } from "../../components/lib/validSession";
import { sortByNumber, sortList } from "../../components/lib/functions";
import logger from "../../utils/logger";

export class DriversPageProfileFunctions {

    constructor(showAlert) {
        this.showAlert = showAlert;
    }

    // Fetch all profiles and process them into different lists
    async fetchProfiles(setIsLoading, setDrivers, setAdmins, setServices, setCustomerProfiles, setWarehouseStaff, setInactive, setLicenceOverdue, setLicenceDue, logout, navigate) {
        setIsLoading(true);
        
        // Check for valid session to stop a loop on component mount
        if (!validSession()) {
            setIsLoading(false);
            this.showAlert({
                message: 'Session expired, please login again',
                type: 'danger',
                buttons: [{
                    label: 'Login',
                    type: 'danger',
                    onClick: () => {
                        logout();
                        navigate('/');
                        logger.log("Session expired, failed to fetch profiles");
                    }
                }],
            });
            return;
        }

        try {
            const result = await fetch_all_profiles();

            if (result.status === 200) {
                const profiles = result.data || [];

                // Sort profiles by name
                const sortedProfiles = sortList(profiles, 'name');

                // Filter active profiles
                const activeProfiles = sortedProfiles.filter(profile => profile.status === 'active');

                // Set account lists
                setDrivers(activeProfiles.filter(profile => profile.group === 'DRIVER'));
                setAdmins(activeProfiles.filter(profile => profile.group === 'ADMIN'));
                setServices(activeProfiles.filter(profile => profile.group === 'SERVICE'));
                setCustomerProfiles(activeProfiles.filter(profile => profile.group === 'CUSTOMER'));
                setWarehouseStaff(activeProfiles.filter(profile => profile.group === 'WAREHOUSE'));
                setInactive(sortedProfiles.filter(profile => profile.status === 'inactive'));

                // Check for overdue and due licences
                this.processLicenceChecks(profiles, setLicenceOverdue, setLicenceDue);

                setIsLoading(false);

            } else {
                setIsLoading(false);
                this.showAlert({
                    message: 'Something went wrong, please try again',
                    type: 'danger',
                    buttons: [{
                        label: 'Login',
                        type: 'danger',
                        onClick: () => {
                            logout();
                            navigate('/');
                            logger.log("Session expired, please login again");
                        }
                    }],
                });
            }
        } catch (error) {
            setIsLoading(false);
            this.showAlert({
                message: 'Error fetching profiles',
                type: 'danger',
                buttons: [{ label: 'Close' }],
            });
            logger.error('Error fetching profiles: ', error);
        }
    }

    // Process licence checks for overdue and due dates
    processLicenceChecks(profiles, setLicenceOverdue, setLicenceDue) {
        const overdue = [];
        const due = [];

        for (const record of profiles) {
            const today = new Date();
            const checks = ['licence_expiry', 'tacho_expiry', 'cpc_expiry'];

            for (let check of checks) {
                const value = record[check];
                if (value) {
                    const dueDate = new Date(value);
                    const diffDays = (dueDate - today) / (1000 * 60 * 60 * 24);
                    if (dueDate < today) {
                        overdue.push(record.id);
                    } else if (diffDays < 28) {
                        due.push(record.id);
                    }
                }
            }
        }
        
        setLicenceOverdue(overdue);
        setLicenceDue(due);
    }

    // Fetch vehicles from backend
    async fetchVehicles(setAllVehicles) {
        try {
            const result = await fetch_all_vehicles();
            if (result && result.status === 200) {
                const vehicleData = result.data || [];
                const filteredVehicles = vehicleData.filter(vehicle => vehicle.status === 'active');
                const units = filteredVehicles.filter(vehicle => vehicle.type === 'UNIT');
                const rigids = filteredVehicles.filter(vehicle => vehicle.type === 'RIGID');
                const vehicles = [...units, ...rigids];
                setAllVehicles(vehicles || []);
            } else {
                setAllVehicles([]);
                logger.info('Failed to fetch vehicles (likely permission restricted):', result?.status);
            }
        } catch (error) {
            logger.error('Error fetching vehicles: ', error);
            setAllVehicles([]);
        }
    }

    // Reset password for selected account
    async handleResetPassword(selectedAccount, setIsLoading, setSelectedAccount, setActiveTab, fetchProfiles) {
        setIsLoading(true);
        try {
            const data = {
                email: selectedAccount.email,
                uuid: selectedAccount.id
            };
            const { message, status } = await reset_password(data);

            if (status === 200) {
                setIsLoading(false);
                this.showAlert({
                    message: message,
                    type: 'success',
                    buttons: [{
                        label: 'Close',
                        onClick: () => {
                            setSelectedAccount(null);
                            setActiveTab('drivers');
                            fetchProfiles(); // refresh the list
                        }
                    }],
                });
            } else {
                setIsLoading(false);
                this.showAlert({
                    message: 'Failed to update password',
                    type: 'danger',
                    buttons: [{ label: 'Retry', type: 'danger' }],
                });
            }
        } catch (error) {
            setIsLoading(false);
            this.showAlert({
                message: 'Error resetting password',
                type: 'danger',
                buttons: [{ label: 'Retry', type: 'danger' }],
            });
            logger.error('Error resetting password: ', error);
        }
    }

    // Reset 2FA (TOTP) for selected ADMIN account — dev only
    async handleResetTotp(selectedAccount, setIsLoading, showAlert) {
        setIsLoading(true);
        try {
            const { message, status } = await reset_totp(selectedAccount.id);
            setIsLoading(false);
            if (status === 200) {
                showAlert({
                    message: `2FA has been reset for ${selectedAccount.name} ${selectedAccount.surname}. They will be prompted to re-enroll on next login.`,
                    type: 'success',
                    buttons: [{ label: 'Close' }],
                });
            } else {
                showAlert({
                    message: message || 'Failed to reset 2FA',
                    type: 'danger',
                    buttons: [{ label: 'Close' }],
                });
            }
        } catch (error) {
            setIsLoading(false);
            showAlert({
                message: 'Error resetting 2FA',
                type: 'danger',
                buttons: [{ label: 'Close' }],
            });
            logger.error('Error resetting TOTP:', error);
        }
    }

    // Update account profile
    async handleUpdate(updatedData, setIsLoading, setSelectedAccount, setActiveTab, fetchProfiles) {
        setIsLoading(true);
        try {
            const result = await update_profile(updatedData);

            if (result.status === 200) {
                setIsLoading(false);
                this.showAlert({
                    message: `Account for ${result.data.name} updated`,
                    type: 'success',
                    buttons: [{
                        label: 'Close',
                        onClick: () => {
                            setSelectedAccount(null);
                            setActiveTab('drivers');
                            fetchProfiles(); // refresh the list
                        }
                    }],
                });
            } else {
                setIsLoading(false);
                this.showAlert({
                    message: 'Failed to update account',
                    type: 'danger',
                    buttons: [{ label: 'Retry', type: 'danger' }],
                });
            }
        } catch (error) {
            setIsLoading(false);
            this.showAlert({
                message: 'Error updating account',
                type: 'danger',
                buttons: [{ label: 'Retry', type: 'danger' }],
            });
            logger.error('Error updating account: ', error);
        }
    }

    // Suspend or activate account
    async handleSuspend(account, setIsLoading, setSelectedAccount, setActiveTab, fetchProfiles) {
        setIsLoading(true);
        try {
            const newStatus = account.status === 'active' ? 'inactive' : 'active';
            const result = await suspend_profile(account.id, newStatus);

            if (result.status === 200) {
                setIsLoading(false);
                this.showAlert({
                    message: `Account now ${result.data.status}`,
                    type: 'success',
                    buttons: [{
                        label: 'Close',
                        onClick: () => {
                            setSelectedAccount(null);
                            setActiveTab('drivers');
                            fetchProfiles(); // refresh the list
                        }
                    }],
                });
            } else {
                setIsLoading(false);
                this.showAlert({
                    message: 'Failed to change account status',
                    type: 'danger',
                    buttons: [{ label: 'Retry', type: 'danger' }],
                });
            }
        } catch (error) {
            setIsLoading(false);
            this.showAlert({
                message: 'Error changing account status',
                type: 'danger',
                buttons: [{ label: 'Retry', type: 'danger' }],
            });
            logger.error('Error suspending account: ', error);
        }
    }

    // Check if date expires in next 28 days
    checkDate(expiryDate) {
        const today = new Date();
        const dueDate = new Date(expiryDate);
        const diffDays = (dueDate - today) / (1000 * 60 * 60 * 24);
        return diffDays < 28;
    }

    // fetch all customers
    async fetchCustomers(setCustomers) {
        try {
            const result = await fetch_all_customers();
            if (result && result.status === 200) {
                let customers = result.data.data || [];
                // sort by id
                customers = sortByNumber(customers, 'id');
                // filter to get unique customers by company name
                const uniqueCompanies = new Set();
                const uniqueCustomers = customers.filter(customer => {
                    if (uniqueCompanies.has(customer.company)) {
                        return false; // Skip this customer (company already exists)
                    } else {
                        uniqueCompanies.add(customer.company);
                        return true; // Keep this customer (first occurrence of this company)
                    }
                });
                // sort new list by company
                const sortedCustomers = sortList(uniqueCustomers, 'company')
                // uniqueCustomers is an array of customer objects (one per unique company)
                setCustomers(sortedCustomers);
                // Log the fetched customers array
            } else {
                setCustomers([]);
                logger.info('Failed to fetch customers (likely permission restricted):', result?.status);
            }
        } catch (error) {
            setCustomers([]);
            logger.error('Error fetching customers: ', error);
        }
    }
}
